import { useContext } from 'react';
import styled, { useTheme } from 'styled-components';
import { AddressModel, DashFeaturesEnum } from 'dash-common';
import {
  Flex,
  Thumbnail,
  Card,
  Checkbox,
  Button,
  BulkActionsContext,
  TooltipWrapper,
  ModalContext,
  DropDown,
  useScreenResolution,
  AddressLabel,
  ViewerContext,
  useCheckIfSubscriptionFeatureActiveBroker,
  Badge,
  Box,
} from 'dash-ui';
import { useTranslation } from 'react-i18next';

import { PortfolioFragment, Property } from 'gql/generated.graphql';
import PortfolioLockMask from 'containers/portfolio/PortfolioLockMask/PortfolioLockMask';
import { getPreviewPortfolioPropertyLink } from 'common/helpers/portfolio';
import VerifiedBadge from 'common/components/VerifiedBadge/VerifiedBadge';

import { formatPropertyPrice } from './PortfolioPropertyCardItem.helpers';
import {
  StyledText,
  VerifiedBadgeWrapper,
} from 'containers/properties/PropertyCard/PropertyCard.styles';
import PropertyUnitsRow from 'containers/properties/PropertyCard/components/PropertyUnitsRow';
import ProjectStatusRow from 'containers/properties/PropertyCard/components/ProjectStatusRow';

const CardStyle = styled(Card)`
  position: relative;
  &:hover {
    #buttons-container > div:last-child {
      display: flex;
    }
  }
`;
const ButtonsContainer = styled(Flex)`
  position: absolute;
  z-index: 2;
  bottom: ${({ theme }) => theme.space * 3}px;
  left: ${({ theme }) => theme.space * 3}px;

  @media ${({ theme }) => theme.devices.laptop} {
    & > div:last-child {
      display: none;
    }
  }
  @media ${({ theme }) => theme.devices.desktop} {
    & > div:last-child {
      display: none;
    }
  }
`;

export const ImageContainer = styled(Box)`
  position: relative;

  :hover {
    & > div:last-child {
      display: flex;
    }
  }
`;

const PortfolioPropertyCardItem = ({
  portfolioItem,
}: {
  portfolioItem: PortfolioFragment;
}) => {
  const theme = useTheme();
  const { t } = useTranslation('pages');
  const { selectedItems, setSelectedItems } = useContext(BulkActionsContext);
  const { property, _id: portfolioId } = portfolioItem;
  const checked = selectedItems.some((elem) => elem._id === portfolioId);
  const { toggleModal, setPayload } = useContext(ModalContext);
  const { broker, profile } = useContext(ViewerContext);
  const {
    devices: { isMobile, isTablet, isLaptop, isDesktop },
  } = useScreenResolution();
  const { checkFeatureAccess } = useCheckIfSubscriptionFeatureActiveBroker([
    DashFeaturesEnum.PORTFOLIO_CREATION_AND_SHARING,
  ]);

  const onMove = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    if (checkFeatureAccess()) {
      setPayload({ selectedItems: [portfolioItem] });
      toggleModal(true, 'MovePropertyToFolderModal');
    }
  };

  const onRemove = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    if (checkFeatureAccess()) {
      setPayload({
        portfolioId: portfolioItem._id,
      });
      toggleModal(true, 'RemovePropertyFromPortfolio');
    }
  };

  const { formattedPrice, ...priceRange } = formatPropertyPrice(
    property?.priceRange
  );

  return (
    <CardStyle
      data-testid="property-card"
      w="100%"
      flexDirection="column"
      onClick={() => {
        window?.open(
          getPreviewPortfolioPropertyLink(
            { ...portfolioItem, name: portfolioItem.property?.name },
            broker,
            profile
          ),
          '_blank'
        );
      }}
    >
      <VerifiedBadgeWrapper>
        {property?.marketArea && (
          <Badge
            title={property.marketArea.name}
            bgColor={theme.colors.setOpacity(theme.colors.b50, 70)}
            titleColor={theme.colors.white}
            semibold={false}
            py={2}
            mr={2}
            square={true}
          />
        )}
        {property?.isMarkedAsVerified && <VerifiedBadge isSmall={true} />}
      </VerifiedBadgeWrapper>
      <ImageContainer h={182} w="100%">
        <ButtonsContainer
          py={2}
          w="95%"
          justifyContent="space-between"
          id="buttons-container"
          alignItems="center"
        >
          <Flex onClick={(e) => e.stopPropagation()} alignItems="center">
            <Checkbox
              m={1}
              mx={2}
              py={2}
              checked={checked}
              onChange={(checked) => setSelectedItems(checked, portfolioItem)}
            />
          </Flex>
          {(isMobile || isTablet) && (
            <Flex>
              <DropDown
                Anchor={() => <Button square icon="dots" />}
                variant="dark"
                placementHorizontal="left"
                items={[
                  {
                    key: 'tab2',
                    icon: 'driveFileMove',
                    children: t('pages:portfolioDetailsPage:labels:move'),
                    onClick: onMove,
                  },
                  {
                    key: 'tab3',
                    icon: 'trash',
                    children: t('common:labels:delete'),
                    onClick: onRemove,
                  },
                ]}
              />
            </Flex>
          )}
          {(isLaptop || isDesktop) && (
            <Flex>
              <TooltipWrapper
                placement="bottom"
                content={t('pages:portfolioDetailsPage:labels:moveProperty')}
              >
                <Button
                  data-tooltip-id={`${property?._id}-move-property`}
                  mr={2}
                  square
                  icon="driveFileMove"
                  onClick={onMove}
                />
              </TooltipWrapper>
              <TooltipWrapper
                placement="bottom"
                content={t('pages:portfolioDetailsPage:labels:removeProperty')}
              >
                <Button
                  onClick={onRemove}
                  square
                  colorVariation="danger"
                  icon="trash"
                />
              </TooltipWrapper>
            </Flex>
          )}
        </ButtonsContainer>
        <Thumbnail
          file={property?.mainPropertyMediaFile?.files[0]}
          placeholderProps={{
            borderRadius: [8, 8, 0, 0],
          }}
        />
        <PortfolioLockMask w="100%" h="100%" portfolio={portfolioItem} />
      </ImageContainer>
      <Flex
        px={[4, 3, 4, 4]}
        py={2}
        flexDirection="column"
        justifyContent="space-between"
        maxWidth={'100%'}
      >
        <Flex w="100%" flexDirection="column">
          <TooltipWrapper content={property?.name}>
            <StyledText
              clamp={1}
              lg={!isTablet}
              semibold
              color={theme.colors.n90}
            >
              {property?.name}
            </StyledText>
          </TooltipWrapper>
          <PropertyUnitsRow property={property as Property} />
        </Flex>
        <Flex flexDirection="column" w="100%" mt={4} mb={2}>
          <Flex w="100%" flex={1}>
            <AddressLabel
              color={theme.colors.n80}
              address={property?.location.address as AddressModel}
            />
          </Flex>
          <Flex flex={1} w="100%">
            <ProjectStatusRow property={property as Property} />
          </Flex>
        </Flex>
      </Flex>
    </CardStyle>
  );
};

export default PortfolioPropertyCardItem;
